import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SheetCell, SheetTab } from '@itsappy/shared';
import { useSocket } from '@/realtime/socketContext';
import { useAddSheetTab, useDeleteSheetTab, useRenameSheetTab } from '@/hooks/queries';
import { API_URL } from '@/lib/config';
import { tokenStore } from '@/lib/api';
import { toast } from '@/store/ui';
import { useConfirm } from '@/components/ui/confirmContext';
import { Modal, Spinner } from '@/components/ui';
import { DownloadIcon, PlusIcon, TrashIcon } from '@/components/ui/icons';
import { cn } from '@/lib/utils';
import { SheetGrid } from './SheetGrid';

/**
 * A whole workbook: the socket subscription, the tab strip, and the grid.
 *
 * The tabs come from `sheet:subscribe` rather than from the REST payload, so
 * that opening the sheet and starting to receive other people's edits are the
 * same event — there is no window where the grid is on screen but not yet
 * listening, which is where a lost first keystroke would come from.
 */
export function SheetWorkbook({
  sheetId,
  canManage,
  name,
  className,
}: {
  sheetId: string;
  canManage: boolean;
  name: string;
  className?: string;
}) {
  const { socket } = useSocket();
  const confirm = useConfirm();
  const addTab = useAddSheetTab();
  const renameTab = useRenameSheetTab();
  const deleteTab = useDeleteSheetTab();

  const [tabs, setTabs] = useState<SheetTab[] | null>(null);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  /* Adding and renaming a tab both ask for one line of text, so they share
     one dialog rather than each growing their own. */
  const [naming, setNaming] = useState<{ tabId?: string; value: string } | null>(null);

  useEffect(() => {
    if (!socket) return;
    let cancelled = false;
    setTabs(null);
    setError(null);

    socket.emit('sheet:subscribe', { sheetId }, (ack) => {
      if (cancelled) return;
      if (!ack.ok) {
        setError(ack.error ?? 'This sheet could not be opened');
        return;
      }
      setTabs(ack.tabs ?? []);
      setCanEdit(Boolean(ack.canEdit));
      setActiveTabId((current) => current ?? ack.tabs?.[0]?.id ?? null);
    });

    return () => {
      cancelled = true;
      socket.emit('sheet:unsubscribe', { sheetId });
    };
  }, [socket, sheetId]);

  /* A tab added or renamed elsewhere arrives without its cells; the strip is
     updated in place so the grid the viewer is in does not get thrown away. */
  useEffect(() => {
    if (!socket) return;
    const onTabs = (p: { sheetId: string; tabs: Omit<SheetTab, 'cells'>[] }) => {
      if (p.sheetId !== sheetId) return;
      setTabs((current) =>
        p.tabs.map((meta) => ({
          ...meta,
          cells: current?.find((tab) => tab.id === meta.id)?.cells ?? [],
        })),
      );
      setActiveTabId((current) =>
        current && p.tabs.some((tab) => tab.id === current) ? current : (p.tabs[0]?.id ?? null),
      );
    };
    socket.on('sheet:tabs', onTabs);
    return () => {
      socket.off('sheet:tabs', onTabs);
    };
  }, [socket, sheetId]);

  /**
   * Keeps this component's copy of a tab in step with the grid's.
   *
   * Without it, switching tabs and coming back would re-mount the grid from
   * the cells that arrived at subscribe time and visibly undo every edit made
   * since — the work is still safe on the server, but watching it vanish is
   * not something a person will trust twice.
   */
  const handleCellsChanged = useCallback((tabId: string, cells: SheetCell[]) => {
    setTabs((current) =>
      current?.map((tab) => (tab.id === tabId ? { ...tab, cells } : tab)) ?? current,
    );
  }, []);

  const activeTab = useMemo(
    () => tabs?.find((tab) => tab.id === activeTabId) ?? tabs?.[0],
    [tabs, activeTabId],
  );

  /**
   * Downloads the live workbook.
   *
   * The export is an authenticated route, so it is fetched and handed to the
   * browser as a blob rather than opened as a link — a bare `<a href>` would
   * arrive without the token and come back a 401.
   */
  const download = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${API_URL}/sheets/${sheetId}/export`, {
        headers: {
          Authorization: `Bearer ${tokenStore.access ?? ''}`,
          ...(tokenStore.orgId ? { 'X-Org-Id': tokenStore.orgId } : {}),
        },
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${name}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('That sheet could not be exported');
    } finally {
      setExporting(false);
    }
  };

  if (error) {
    return (
      <div className="grid h-full place-items-center px-4 text-center">
        <p className="text-xs text-slate-500">{error}</p>
      </div>
    );
  }

  if (!tabs || !activeTab) {
    return (
      <div className="grid h-full place-items-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className={cn('flex min-h-0 flex-col', className)}>
      <SheetGrid
        key={activeTab.id}
        sheetId={sheetId}
        tab={activeTab}
        canEdit={canEdit}
        onCellsChanged={handleCellsChanged}
        className="min-h-0 flex-1"
      />

      <div className="flex items-center gap-1 border-t border-surface-3 bg-surface-2 px-1.5 py-1">
        <div className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTabId(tab.id)}
              onDoubleClick={() => canEdit && setNaming({ tabId: tab.id, value: tab.name })}
              className={cn(
                'shrink-0 rounded-md px-2.5 py-1 text-xs transition-colors',
                tab.id === activeTab.id
                  ? 'bg-surface-1 font-medium text-ink'
                  : 'text-slate-400 hover:bg-surface-3 hover:text-ink',
              )}
            >
              {tab.name}
            </button>
          ))}

          {canEdit && (
            <button
              type="button"
              aria-label="Add tab"
              onClick={() => setNaming({ value: `Sheet ${tabs.length + 1}` })}
              className="shrink-0 rounded-md p-1 text-slate-500 transition-colors hover:bg-surface-3 hover:text-ink"
            >
              <PlusIcon className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {canEdit && canManage && tabs.length > 1 && (
          <button
            type="button"
            aria-label="Delete this tab"
            onClick={async () => {
              const ok = await confirm({
                title: `Delete "${activeTab.name}"?`,
                description: 'Everything on this tab goes with it, for everyone.',
                confirmLabel: 'Delete tab',
                destructive: true,
              });
              if (!ok) return;
              try {
                await deleteTab.mutateAsync({ id: sheetId, tabId: activeTab.id });
              } catch {
                toast.error('That tab could not be deleted');
              }
            }}
            className="shrink-0 rounded-md p-1 text-slate-500 transition-colors hover:bg-surface-3 hover:text-rose-400"
          >
            <TrashIcon className="h-3.5 w-3.5" />
          </button>
        )}

        <button
          type="button"
          onClick={download}
          disabled={exporting}
          className="flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-slate-400 transition-colors hover:bg-surface-3 hover:text-ink disabled:opacity-50"
        >
          {exporting ? <Spinner className="h-3 w-3" /> : <DownloadIcon className="h-3.5 w-3.5" />}
          Export
        </button>
      </div>

      <Modal
        open={Boolean(naming)}
        onClose={() => setNaming(null)}
        title={naming?.tabId ? 'Rename tab' : 'New tab'}
        width="sm"
      >
        <form
          onSubmit={async (event) => {
            event.preventDefault();
            const value = naming?.value.trim();
            if (!value) return;
            const pending = naming;
            setNaming(null);
            try {
              if (pending?.tabId) {
                await renameTab.mutateAsync({ id: sheetId, tabId: pending.tabId, name: value });
              } else {
                await addTab.mutateAsync({ id: sheetId, name: value });
              }
            } catch {
              toast.error(pending?.tabId ? 'That tab could not be renamed' : 'That tab could not be added');
            }
          }}
        >
          <input
            autoFocus
            value={naming?.value ?? ''}
            onChange={(event) =>
              setNaming((current) => (current ? { ...current, value: event.target.value } : current))
            }
            maxLength={60}
            placeholder="Tab name"
            className="w-full rounded-lg bg-surface-2 px-3 py-2 text-sm text-ink outline-none ring-1 ring-surface-3 focus:ring-brand-500"
          />
          <div className="mt-3 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setNaming(null)}
              className="rounded-lg px-3 py-1.5 text-sm text-slate-400 transition-colors hover:bg-surface-3 hover:text-ink"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!naming?.value.trim()}
              className="rounded-lg bg-brand-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-brand-400 disabled:opacity-50"
            >
              {naming?.tabId ? 'Rename' : 'Add tab'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
